import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../../App.css";
import { useAuth } from "../../../context/AuthContext";
import adminIds from "../../../analytics";

function generateProgressBar(current, max = 120) {
  const totalBlocks = 12;
  const safeCurrent = Math.max(0, Math.min(Number(current || 0), max));
  const filledBlocks = Math.round((safeCurrent / max) * totalBlocks);
  const emptyBlocks = totalBlocks - filledBlocks;
  return `${"█".repeat(filledBlocks)}${"░".repeat(emptyBlocks)} [${safeCurrent}]`;
}

function formatStatusValue(value) {
  if (!value) return "no";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

const StatCell = ({ label, value }) => (
  <div className="profile-stat-cell">
    <div className="profile-stat-label">{label}</div>
    <div className="profile-stat-value">{value}</div>
  </div>
);

const UserProfile = () => {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState(null);

  const isOffice = useMemo(() => {
    return !!(user && adminIds.includes(user.id));
  }, [user]);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      try {
        setError(null);
        setProfileLoading(true);

        const res = await fetch("/api/user/profile", {
          credentials: "include",
          cache: "no-store",
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        if (mounted) {
          setProfile(data);
        }
      } catch (err) {
        console.error("Failed to load user profile:", err);
        if (mounted) {
          setError(err.message || "Failed to load profile");
        }
      } finally {
        if (mounted) {
          setProfileLoading(false);
        }
      }
    };

    if (!loading && user) {
      loadProfile();
    } else if (!loading && !user) {
      setProfileLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [loading, user]);

  if (loading || profileLoading) {
    return (
      <div className="profile-shell">
        <div className="profile-panel">
          <h1>User Profile</h1>
          <h2>Loading command profile...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-shell">
        <div className="profile-panel">
          <h1>Access Denied</h1>
          <h2>You must be logged in to view this page.</h2>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-shell">
        <div className="profile-panel">
          <h1>User Profile</h1>
          <h2>{error || "Profile unavailable."}</h2>
        </div>
      </div>
    );
  }

  const { discord, wallet } = profile;
  const displayName = discord.global_name || discord.username;
  const avatarUrl = discord.avatar
    ? `https://cdn.discordapp.com/avatars/${discord.id}/${discord.avatar}.png?size=256`
    : null;

  const magicBar = generateProgressBar(wallet.magic, 120);
  const healthBar = generateProgressBar(wallet.health, 120);

  return (
    <div className="profile-shell">
      <div className="profile-panel">
        <div className="profile-topbar">
          <div>
            <div className="profile-kicker">ONLINE DOSSIER</div>
            <h1 className="profile-title">{displayName}</h1>
            <h2 className="profile-subtitle">
              {discord.username}&nbsp;&nbsp;•&nbsp;&nbsp;{wallet.kills}/{wallet.deaths}/{wallet.heals}
            </h2>
          </div>

          <div className="profile-top-actions">
            {isOffice && (
              <Link to="/analytics" className="profile-admin-button">
                ADMIN
              </Link>
            )}

            <Link to="/tools" className="profile-admin-button">
                TOOLS
              </Link>
          </div>
        </div>

        <div className="profile-hero">
          <div className="profile-avatar-frame">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="profile-avatar" />
            ) : (
              <div className="profile-avatar-fallback">{displayName.charAt(0)}</div>
            )}
          </div>

          <div className="profile-identity-card">
            <div className="profile-identity-line">
              <span className="profile-identity-label">Discord ID</span>
              <span className="profile-identity-value">{discord.id}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">House</span>
              <span className="profile-identity-value">{wallet.house}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">Pass</span>
              <span className="profile-identity-value">{wallet.pass ? "Yes" : "No"}</span>
            </div>
            <div className="profile-identity-line">
              <span className="profile-identity-label">Briefcase</span>
              <span className="profile-identity-value">{wallet.briefcase ? "Held" : "No"}</span>
            </div>
          </div>

          <div className="profile-currency-card">
            <div className="profile-currency-block">
              <div className="profile-currency-label">POGS</div>
              <div className="profile-currency-value">{wallet.pogs}</div>
            </div>
            <div className="profile-currency-block">
              <div className="profile-currency-label">VAULT</div>
              <div className="profile-currency-value">{wallet.vault}</div>
            </div>
          </div>
        </div>

        <div className="profile-grid">
          <StatCell label="XP" value={wallet.xp} />
          <StatCell label="Horcruxes" value={wallet.horcruxes} />
          <StatCell label="Merits" value={wallet.merits} />

          <StatCell label="Strikes" value={wallet.strikes} />
          <StatCell label="Wand" value={wallet.wand} />
          <StatCell label="Spell" value={wallet.spell} />

          <StatCell label="Charm" value={wallet.charm} />
          <StatCell label="Weaponry" value={wallet.weaponry} />
          <StatCell label="Protection" value={wallet.protection} />

          <StatCell label="Hardware" value={wallet.hardware} />
          <StatCell label="Gadget" value={wallet.gadget} />
          <StatCell label="Ultimate" value={wallet.ultimate} />

          <StatCell label="Used Ult" value={formatStatusValue(wallet.usedUlt) || "Not Used"} />
          <StatCell label="Disabled" value={formatStatusValue(wallet.disabled)} />
          <StatCell label="Detained" value={formatStatusValue(wallet.detained)} />

          <StatCell label="Wasted" value={formatStatusValue(wallet.wasted)} />
        </div>

        <div className="profile-bars">
          <div className="profile-bar-block">
            <div className="profile-bar-label">Magic</div>
            <div className="profile-bar-track">
              <div
                className="profile-bar-fill"
                style={{ width: `${Math.max(0, Math.min((wallet.magic / 120) * 100, 100))}%` }}
              />
            </div>
            <div className="profile-bar-readout">{magicBar}</div>
          </div>

          <div className="profile-bar-block">
            <div className="profile-bar-label">Health</div>
            <div className="profile-bar-track">
              <div
                className="profile-bar-fill"
                style={{ width: `${Math.max(0, Math.min((wallet.health / 120) * 100, 100))}%` }}
              />
            </div>
            <div className="profile-bar-readout">{healthBar}</div>
          </div>
        </div>

        {wallet.briefcase && (
          <div className="profile-briefcase-alert">
            BRIEFCASE STATUS: ACTIVE HOLDER
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;